
import React, { useEffect, useMemo, useState, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Col, Container, Row } from "reactstrap";
import {
  PieChart,
  Pie,
  Cell,
  Sector,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import moment from "moment/moment";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import * as XlsxPopulate from "xlsx-populate/browser/xlsx-populate";
import company from "../../../assets/image/icons/company.svg";
import activeReport from "../../../assets/image/icons/active-report.svg";
import branches from "../../../assets/image/icons/branches.svg";
import tick from "../../../assets/image/icons/tick.svg";
import { requestApi } from "../../../request-api";
import Dropdown from "../../layouts/dropdown/Dropdown";
import ExportDropdown from "../../layouts/dropdown/ExportDropdown";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Highcharts3D from "highcharts/highcharts-3d";
// Initialize Highcharts 3D module safely across ESM/CJS builds
try {
  const add3DModule = Highcharts3D && (Highcharts3D.default || Highcharts3D);
  if (typeof add3DModule === "function") {
    add3DModule(Highcharts);
  }
} catch (err) {
  // no-op: module init failed
}

function Dashboard() {
  const [user, setUser] = useState({});
  const [reportData, setReportData] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [complinces, setComplinces] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [locationSearchName, setLocationSearchName] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companySearchName, setCompanySearchName] = useState("");
  const [companies, setCompanies] = useState([]);
  const [activeFilter, setActiveFilter] = useState("");
  const [complianceBarData, setComplianceBarData] = useState([]);
  const [totalCompliance, setTotalCompliance] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [circleChart, setCircleChart] = useState([]);
  const [criticalityRiskFilter, setCriticalityRiskFilter] = useState(null);
  const [criticalityRiskData, setCriticalityRiskData] = useState([]);
  const [statisticsRiskFilter, setStaticticsRiskFilter] = useState(null);
  const [statisticsRiskData, setStatisticsRiskData] = useState([]);
  const [companyOverviewFilter, setCompanyOverviewFilter] = useState(null);
  const [companyOverviewData, setCompanyOverviewData] = useState([]);
  const [complianceCategoryFilter, setComplianceCategoryFilter] =
    useState(null);
  let [activeIndex, setActiveIndex] = useState(0);
  const [loadingCount, setLoadingCount] = useState(0);
  const loading = loadingCount > 0;
  const [totalCompliancesCount, setTotalCompliancesCount] = useState(0);
  const dateDropdownRef = useRef(null);
  const [filterDateWise, setFilterDateWise] = useState({
    endDate: new Date(),
    startDate: new Date(),
  });
  const [totalCount, setTotalCount] = useState({
    totalCompanies: "",
    totalBranches: "",
    totalActiveReports: "",
    totalClosedReports: "",
  });
  const [format, setFormat] = useState("pdf");
  const [period, setPeriod] = useState("Monthly");
  const formats = [
    { label: "Pdf", icon: "bi-file-earmark-pdf", value: "pdf" },
    { label: "Excel", icon: "bi-file-earmark-excel", value: "excel" },
  ];
  const periods = ["Monthly", "Quarterly", "Semi-Annually", "Annually"];
  const complianceStatuses = [
    { name: "Complied", key: "Complied", fill: "#4CAF50" },
    { name: "Not Complied", key: "Not Complied", fill: "#F44336" },
    { name: "Not Applicable", key: "Not Applicable", fill: "#9ba0a3ff" },
    {
      name: "Partial Complied",
      key: "Partial Complied",
      fill: "#facc15",
    },
     {
      name: "Not Updated",
      key: "Not Updated",
      fill: "#158ffaff",
    },
  ];
  const statusKeys = {
    Complied: "Complied",
    notComplied: "Not Complied",
    notApplicable: "Not Applicable",
    compiledWithException: "Partial Complied",
    notUpdated: "Not Updated",
  };

  const pieOptions = useMemo(() => {
    const baseSeriesData = (circleChart || []).map((item) => ({
      name: item.name,
      y: Number(item.value) || 0,
      color:
        (complianceStatuses.find((s) => s.name === item.name) || {}).fill,
    }));

    // Compute integer percentages that sum to 100
    const totalY = baseSeriesData.reduce((s, p) => s + (p.y || 0), 0);
    let seriesData = baseSeriesData;
    if (totalY > 0) {
      const withCalcs = baseSeriesData.map((p, idx) => {
        const raw = (p.y / totalY) * 100;
        const rounded = Math.floor(raw);
        const remainder = raw - rounded;
        return { idx, raw, rounded, remainder };
      });

      let sumRounded = withCalcs.reduce((s, r) => s + r.rounded, 0);
      let diff = 100 - sumRounded;

      // Distribute the remaining percentage points to slices with largest remainders
      const byRemainderDesc = [...withCalcs].sort(
        (a, b) => b.remainder - a.remainder
      );
      let i = 0;
      while (diff > 0 && i < byRemainderDesc.length) {
        byRemainderDesc[i].rounded += 1;
        diff -= 1;
        i += 1;
      }

      // If we overshot due to flooring, adjust by subtracting from smallest remainders but keep >= 0
      if (diff < 0) {
        const byRemainderAsc = [...withCalcs].sort(
          (a, b) => a.remainder - b.remainder
        );
        let j = 0;
        while (diff < 0 && j < byRemainderAsc.length) {
          if (byRemainderAsc[j].rounded > 0) {
            byRemainderAsc[j].rounded -= 1;
            diff += 1;
          }
          j += 1;
        }
      }

      // Attach customPercent back to points
      seriesData = baseSeriesData.map((p, idx) => ({
        ...p,
        customPercent:
          (withCalcs.find((r) => r.idx === idx) || {}).rounded || 0,
      }));
    } else {
      seriesData = baseSeriesData.map((p) => ({ ...p, customPercent: 0 }));
    }

    return {
      chart: {
        type: "pie",
        backgroundColor: "transparent",
        options3d: { enabled: true, alpha: 45, beta: 0 },
        height: 287,
      },
      title: { text: null },
      tooltip: {
        pointFormatter: function () {
          const percent =
            this.customPercent ?? Math.round(this.percentage || 0);
          return `<b>${percent}%</b>`;
        },
        useHTML: true,
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          innerSize: 0,
          depth: 45,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            formatter: function () {
              const percent =
                this.point.customPercent ??
                Math.round(this.point.percentage || 0);
              return `${percent}%`;
            },
            style: { textOutline: "none", fontSize: "12px", textAlign: "center" },
          },
          showInLegend: false,
        },
      },
      credits: { enabled: false },
      series: [
        {
          name: "Overview",
          data: seriesData,
        },
      ],
    };
  }, [circleChart, complianceStatuses]);



  const startLoading = () => setLoadingCount((prev) => prev + 1);
  const stopLoading = () => setLoadingCount((prev) => prev - 1);

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        const data = await requestApi("/superadmin/userData", {
          method: "get",
        });
        setUser(data.data);
      } catch (error) {
        toast.error("Something went wrong while fetching user details.");
      } finally {
        stopLoading();
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await requestApi(`/report/reportscomp`, { method: "get" });
        const count = Array.isArray(res) ? res.length : 0;
        setTotalCompliancesCount(count);
      } catch (error) {
        // silently ignore; keep count at 0
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      startLoading();

      try {
        const { reports, statusCode } = await requestApi(
          "/auditor/getReportOnDashboardComp",
          {
            method: "get",
          }
        );

        if (statusCode === 200) {
          const allLegislations = reports.flatMap(
            (item) => item.legislation || []
          );

          const uniqueLocations = [
            ...new Set(allLegislations.map((l) => l.location).filter(Boolean)),
          ];
          const uniqueCompanies = [
            ...new Set(
              allLegislations.map((l) => l.company_name).filter(Boolean)
            ),
          ];

          setLocations(uniqueLocations);
          setCompanies(uniqueCompanies);

          setReportData(reports);
        }
      } catch (error) {
        toast.error("Something went wrong!");
      } finally {
        stopLoading();
      }
    })();
  }, []);

  useEffect(() => {
    if (!Array.isArray(reportData)) return;

    let filtered = reportData;

    // Apply date filter if active (when activeFilter is explicitly set and filterDateWise is valid)
    if (activeFilter && activeFilter !== "" && filterDateWise.startDate && filterDateWise.endDate) {
      const start = new Date(filterDateWise.startDate);
      const end = new Date(filterDateWise.endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      const startTime = start.getTime();
      const endTime = end.getTime();

      filtered = filtered.filter((report) => {
        return report.legislation?.some((leg) => {
          return leg.compliance?.some((comp) => {
            if (!comp.due_date) return false;
            const dueDate = new Date(comp.due_date);
            if (isNaN(dueDate.getTime())) return false;
            const dueTime = dueDate.getTime();
            return dueTime >= startTime && dueTime <= endTime;
          });
        });
      });
    }

    // Apply company and location filters (global filters - always applied when selected)
    if (selectedCompany.length > 0 || selectedLocation.length > 0) {
      filtered = filtered.filter((report) => {
        return report.legislation?.some((leg) => {
          const _cm =
            selectedCompany.length === 0 ||
            selectedCompany.includes(leg.company_name);
          const _lm =
            selectedLocation.length === 0 ||
            selectedLocation.includes(leg.location);

          return _cm && _lm;
        });
      });
    }

    setFilteredReports(filtered);
  }, [selectedCompany, selectedLocation, reportData, activeFilter, filterDateWise]);

  // Clear individual filters when global filters are applied
  useEffect(() => {
    const hasGlobalFilter = 
      (selectedCompany.length > 0) || 
      (selectedLocation.length > 0) || 
      (activeFilter && activeFilter !== "");

    if (hasGlobalFilter) {
      // Clear all individual filters
      setSelectedFilter(null);
      setComplianceCategoryFilter(null);
      setCriticalityRiskFilter(null);
      setStaticticsRiskFilter(null);
      setCompanyOverviewFilter(null);
    }
  }, [selectedCompany, selectedLocation, activeFilter]);

  useEffect(() => {
    if (!Array.isArray(filteredReports)) return;

    const companies = new Set();
    const branches = new Set();

    let activeCount = 0;
    let closedCount = 0;

    filteredReports.forEach(
      (report) => {
        if (report.status === false) activeCount++;
        if (report.status === true) closedCount++;

        report.legislation?.forEach((leg) => {
          if (leg.company_name) companies.add(leg.company_name);
          if (leg.branch_id) branches.add(leg.branch_id);
        });
      },
      [filteredReports]
    );

    const _compliances = filteredReports.flatMap(
      (report) =>
        report.legislation?.flatMap((leg) => leg.compliance || []) || []
    );

    setTotalCount({
      totalCompanies: companies.size,
      totalBranches: branches.size,
      totalActiveReports: activeCount,
      totalClosedReports: closedCount,
    });

    setComplinces(_compliances);
  }, [filteredReports]);

  //use effect sanjay
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dateDropdownRef.current &&
        !dateDropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  useEffect(() => {
    if (!Array.isArray(complinces)) return;

    const _fc = handleComplianceFilter(complinces, selectedFilter);

    const base = complianceStatuses.map((status) => ({
      name: status.name,
      value: _fc.filter((c) => c.current_compliance_status === status.key)
        .length,
      fill: status.fill,
    }));

    const result = [...base];

    setCircleChart(result);
    console.log("Pie chart data :", result);
  }, [selectedFilter, complinces]);

  useEffect(() => {
    const filteredCompliance = handleComplianceFilter(
      complinces,
      criticalityRiskFilter
    );

    const getCount = (status, criticality) =>
      filteredCompliance.filter(
        (c) =>
          c.current_compliance_status === status &&
          c.criticality === criticality
      ).length;

    const computedData = [
      ...new Set(
        complinces.map((item) => item.criticality?.trim()).filter(Boolean)
      ),
    ].map((level) => {
      const statusCounts = complianceStatuses.reduce((acc, status) => {
        acc[status.key] = getCount(status.key, level) || "-";
        return acc;
      }, {});
      return {
        category: level,
        ...statusCounts,
        total: Object.values(statusCounts).reduce(
          (sum, val) => (val !== "-" ? sum + val : sum),
          0
        ),
      };
    });

    const totalRow = {
      category: "Total",
      ...complianceStatuses.reduce((acc, status) => {
        acc[status.key] = computedData.reduce(
          (sum, row) => (row[status.key] !== "-" ? sum + row[status.key] : sum),
          0
        );
        return acc;
      }, {}),
      total: computedData.reduce((sum, row) => sum + row.total, 0),
    };

    const criticalityOrder = ["High", "Medium", "Low"];
    const sortedData = computedData.sort(
      (a, b) =>
        criticalityOrder.indexOf(a.category) -
        criticalityOrder.indexOf(b.category)
    );

    setCriticalityRiskData([...sortedData, totalRow]);
  }, [criticalityRiskFilter, complinces]);

  useEffect(() => {
    if (!Array.isArray(complinces)) return;

    const filtered = filterComplianceByDays(
      complinces,
      complianceCategoryFilter
    );

    const normalizeCategory = (str) =>
      str
        ?.trim()
        .toLowerCase()
        .replace(
          /\w\S*/g,
          (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
        );

    const categories = [
      ...new Set(
        filtered
          .map((i) => normalizeCategory(i.compliance_categorization))
          .filter(Boolean)
      ),
    ];

    const chartData = categories.map((category) => {
      const items = filtered.filter(
        (item) => normalizeCategory(item.compliance_categorization) === category
      );

      const counts = {
        complied: items.filter(
          (i) => i.current_compliance_status === statusKeys.Complied
        ).length,
        notComplied: items.filter(
          (i) => i.current_compliance_status === statusKeys.notComplied
        ).length,
        notApplicable: items.filter(
          (i) => i.current_compliance_status === statusKeys.notApplicable
        ).length,
        notUpdated: items.filter(
          (i) => i.current_compliance_status === statusKeys.notUpdated
        ).length,
        compiledWithException: items.filter(
          (i) =>
            i.current_compliance_status === statusKeys.compiledWithException
        ).length,
      };

      return { name: category, ...counts };
    });

    const total = chartData.reduce(
      (sum, item) =>
        sum +
        (item.complied || 0) +
        (item.notComplied || 0) +
        (item.notApplicable || 0) +
        (item.compiledWithException || 0),
      0
    );

    setComplianceBarData(chartData);
    console.log("Chart data", chartData);
    setTotalCompliance(filtered.length);
  }, [complianceCategoryFilter, complinces]);

  const filterComplianceByDays = (data, days) => {
    if (!days || isNaN(days)) return data;

    const now = moment();
    const targetDate = moment().subtract(Number(days), "days");
    return data.filter(
      (item) => {
        if (!item.due_date) return false;
        const dueDate = moment(item.due_date);
        if (!dueDate.isValid()) return false;
        // Only show past due dates within the last X days (not future dates)
        return dueDate.isSameOrAfter(targetDate, "day") && dueDate.isSameOrBefore(now, "day");
      }
    );
  };

  const toggleLocationSelection = (item) => {
    setSelectedLocation((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const toggleCompanySelection = (item) => {
    setSelectedCompany((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const toggleDropdown = () => setShowDropdown(!showDropdown);

  const handleComplianceFilter = (compliances, filter) => {
    if (!filter || isNaN(filter) || Number(filter) === 0) {
      return compliances;
    }

    const now = new Date();
    now.setHours(23, 59, 59, 999); // End of today
    const filterDate = new Date(now);
    const days = Number(filter);

    filterDate.setDate(filterDate.getDate() - days);
    filterDate.setHours(0, 0, 0, 0);

    return compliances.filter((compliance) => {
      if (!compliance.due_date) return false;

      const dueDate = new Date(compliance.due_date);
      if (isNaN(dueDate.getTime())) return false;

      // Only show past due dates within the last X days (not future dates)
      return dueDate >= filterDate && dueDate <= now;
    });
  };

  const renderActiveShape = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    payload,
  }) => {
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);

    // 👇 always use payload.fill (from your original data)
    const fill = payload.fill;

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
          {payload.name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={payload.fill}
          className=""
          style={{ fill: payload.fill }}
        />
      </g>
    );
  };

  const [columns] = useState([
    {
      name: "Company Name",
      selector: (row) => row.company_name || "NA",
      sortable: true,
    },
    {
      name: "Total Branches",
      selector: (row) => row.branch_count || "-",
      sortable: true,
    },
    {
      name: "Total Categories",
      selector: (row) => row.category_count || "-",
      sortable: true,
    },
    {
      name: "Total Dues",
      selector: (row) => row.total_dues || "-",
      sortable: true,
    },
    {
      name: "Total Compliances",
      selector: (row) => row.total_compliances,
      sortable: true,
    },
    {
      name: "Approver Name",
      selector: (row) => row.approver || "NA",
      sortable: true,
    },
  ]);

  const criticalityRiskColumns = [
    {
      name: "Criticality",
      selector: (row) => row.category,
      sortable: true,
      style: (row) => ({
        fontWeight: row.category === "Total" ? "bold" : "normal",
        backgroundColor: row.category === "Total" ? "#f3f3f3" : "transparent",
      }),
    },
    ...complianceStatuses.map((status) => ({
      name: status.name,
      selector: (row) => row[status.key],
      sortable: true,
      cell: (row) => row[status.key] ?? "-",
    })),
    {
      name: "Total",
      selector: (row) => row.total,
      sortable: true,
      style: { fontWeight: "bold" },
    },
  ];

  const filteredCompanyCompliance = useMemo(() => {
    const now = moment();

    if (!companyOverviewFilter) return complinces;

    return complinces.filter((item) => {
      if (!item.due_date) return false;
      const dueDate = moment(item.due_date);
      if (!dueDate.isValid()) return false;
      // Only show past due dates within the last X days (not future dates)
      const daysDiff = now.diff(dueDate, "days");
      return daysDiff >= 0 && daysDiff <= parseInt(companyOverviewFilter, 10);
    });
  }, [companyOverviewFilter, complinces]);

  
  useEffect(() => {
    const groupedData = {};

    filteredCompanyCompliance.forEach((item) => {
      const companyKey = item.company_name;

      if (!groupedData[companyKey]) {
        groupedData[companyKey] = {
          company_name: companyKey,
          branch_ids: new Set(),
          compliance_categories: new Set(),
          total_dues: 0,
          total_compliances: 0,
          done_compliances: 0,
          approver: item.approver || "NA",
        };
      }

      groupedData[companyKey].branch_ids.add(item.branch_id);
      groupedData[companyKey].compliance_categories.add(
        item.compliance_categorization
      );
      console.log("Open/Closed", item.open_closed)
      // if (item.open_closed == 1) {
      //   groupedData[companyKey].total_compliances += 1;
      // }
      if (item.open_closed == 1) {
        groupedData[companyKey].done_compliances++;
      }

      if (item.due_date) groupedData[companyKey].total_compliances += 1;
    });

        Object.values(groupedData).forEach((company) => {
      company.total_dues =
        company.total_compliances - company.done_compliances;
    });

    const overviewData = Object.values(groupedData).map((company) => ({
      company_name: company.company_name,
      branch_count: company.branch_ids.size,
      category_count: company.compliance_categories.size,
      total_dues: company.total_dues,
      total_compliances: company.total_compliances,
      approver: company.approver,
    }));

    setCompanyOverviewData(overviewData);
  }, [filteredCompanyCompliance]);

  const exportReport = (data, format, period) => {
    const now = new Date();

    const subtractMonths = (date, numMonths) => {
      const d = new Date(date);
      d.setMonth(d.getMonth() - numMonths);

      if (d.getDate() !== date.getDate()) {
        d.setDate(0);
      }

      return d;
    };

    let monthsBack;
    switch (period) {
      case "Monthly":
        monthsBack = 1;
        break;
      case "Quarterly":
        monthsBack = 3;
        break;
      case "Semi-Annually":
        monthsBack = 6;
        break;
      case "Annually":
        monthsBack = 12;
        break;
      default:
        monthsBack = 0;
    }

    const startDate = subtractMonths(now, monthsBack);
    console.log("startDate", period, startDate);

    let srNo = 1;
    const flatExportData = [];

    data.forEach((entry) => {
      const { id: report_id, created_by, updated_by, legislation = [] } = entry;

      legislation.forEach((leg) => {
        const {
          legislation_name,
          status,
          created_at: legislation_created_at,
          compliance = [],
          location,
          company_name,
          branch_id,
        } = leg;

        const legCreatedAt = new Date(legislation_created_at);
        if (
          isNaN(legCreatedAt) ||
          legCreatedAt < startDate ||
          legCreatedAt > now
        ) {
          return;
        }

        if (!Array.isArray(compliance) || compliance.length === 0) {
          flatExportData.push({
            sr_no: srNo++,
            report_id,
            company_name,
            branch_id,
            location,
            created_by,
            updated_by,
            legislation_name,
            legislation_status: status,
            legislation_created_at,
            compliance: "N/A",
            provision: "N/A",
            compliance_description: "N/A",
            compliance_type: "N/A",
            penalty_description: "N/A",
            approver: "N/A",
            due_date: "N/A",
            criticality: "N/A",
            current_compliance_status: "N/A",
          });
        } else {
          compliance.forEach((comp) => {
            flatExportData.push({
              sr_no: srNo++,
              report_id,
              company_name,
              branch_id,
              location,
              created_by,
              updated_by,
              legislation_name,
              legislation_status: status,
              legislation_created_at,
              compliance: comp.compliance || "N/A",
              provision: comp.provision || "N/A",
              compliance_description: comp.compliance_description || "N/A",
              compliance_type: comp.compliance_type || "N/A",
              penalty_description: comp.penalty_description || "N/A",
              approver: comp.approver || "N/A",
              due_date: comp.due_date
                ? new Date(comp.due_date).toLocaleDateString()
                : "N/A",
              criticality: comp.criticality || "N/A",
              current_compliance_status:
                comp.current_compliance_status || "N/A",
            });
          });
        }
      });
    });

    if (flatExportData.length === 0) {
      toast.error("No data found for the selected period.");
      return;
    }

    if (format === "excel") {
      let lastReportId = null;
      flatExportData.forEach((row) => {
        if (row.report_id === lastReportId) {
          row.report_id = "";
          row.company_name = "";
          row.branch_id = "";
          row.location = "";
          row.created_by = "";
          row.updated_by = "";
        } else {
          lastReportId = row.report_id;
        }
      });

      const worksheet = XLSX.utils.json_to_sheet(flatExportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

      XlsxPopulate.fromDataAsync(wbout)
        .then((workbook) => workbook.outputAsync())
        .then((blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const a = document.createElement("a");
          a.href = url;
          a.download = "Report.xlsx";
          a.click();
          window.URL.revokeObjectURL(url);
        });
    } else if (format === "pdf") {
      const newWindow = window.open();
      if (!newWindow) return;

      const tableHeaders = Object.keys(flatExportData[0] || {});
      const mergedRows = [];
      const rowSpans = {};
      const rendered = new Set();

      flatExportData.forEach((item) => {
        const key = item.report_id;
        if (!rowSpans[key]) {
          rowSpans[key] = flatExportData.filter(
            (i) => i.report_id === key
          ).length;
        }
      });

      flatExportData.forEach((item) => {
        let row = "<tr>";
        tableHeaders.forEach((header) => {
          if (
            [
              "report_id",
              "company_name",
              "branch_id",
              "location",
              "created_by",
              "updated_by",
            ].includes(header)
          ) {
            if (!rendered.has(item.report_id)) {
              row += `<td rowspan="${rowSpans[item.report_id]}">${item[header]
                }</td>`;
            }
          } else {
            row += `<td>${item[header]}</td>`;
          }
        });
        row += "</tr>";
        mergedRows.push(row);
        rendered.add(item.report_id);
      });

      const htmlContent = `
        <html>
          <head>
            <title>Report</title>
            <style>
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 10px;
              }
              th, td {
                border: 1px solid #000;
                padding: 4px;
                text-align: left;
                vertical-align: top;
              }
            </style>
          </head>
          <body>
            <h2>Filtered Report - ${period}</h2>
            <table>
              <thead>
                <tr>${tableHeaders.map((h) => `<th>${h}</th>`).join("")}</tr>
              </thead>
              <tbody>${mergedRows.join("")}</tbody>
            </table>
          </body>
        </html>
      `;

      newWindow.document.write(htmlContent);
      newWindow.document.close();
      newWindow.print();
    }
  };

  const handleDateQuickFilter = (type) => {
    const now = new Date();

    let start = new Date();
    let end = new Date();

    switch (type) {
      case "Today":
        break;

      case "Yesterday":
        start.setDate(now.getDate() - 1);
        end.setDate(now.getDate() - 1);
        break;

      case "Last 7 days":
      case "One week":
        start.setDate(now.getDate() - 6);
        break;

      case "Two weeks":
        start.setDate(now.getDate() - 13);
        break;

      case "Three weeks":
        start.setDate(now.getDate() - 20);
        break;

      case "Last 30 days":
        start.setDate(now.getDate() - 29);
        break;

      case "Monthly":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;

      case "Manual":
        setActiveFilter("Manual");
        return;

      default:
        return;
    }

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    setFilterDateWise({ startDate: start, endDate: end });
    setActiveFilter(type);
    setIsOpen(false); // Close dropdown when quick filter is selected

    // applyDateFilter({ startDate: start, endDate: end });
  };

  const applyDateFilter = (range = filterDateWise) => {
    if (!range.startDate || !range.endDate) return;

    // Update the date filter state - the useEffect will handle the filtering
    setFilterDateWise(range);
    // Set activeFilter to trigger the filtering in useEffect
    if (!activeFilter || activeFilter === "Manual") {
      setActiveFilter("Manual");
    }
    setIsOpen(false);
  };

  const filteredCompliance = useMemo(() => {
    if (!statisticsRiskFilter) return complinces;

    const now = moment();
    return complinces.filter((item) => {
      if (!item.due_date) return false;
      const dueDate = moment(item.due_date);
      if (!dueDate.isValid()) return false;
      // Only show past due dates within the last X days (not future dates)
      const daysDiff = now.diff(dueDate, "days");
      return daysDiff >= 0 && daysDiff <= Number(statisticsRiskFilter);
    });
  }, [statisticsRiskFilter, complinces]);

  useEffect(() => {
    const categories = ["High", "Medium", "Low"];
    const statusLabels = {
      Complied: "Complied",
      "Partial Complied": "Partial",
      "Not Applicable": "NotApplicable",
      "Not Complied": "NonComplied",
      "Not Updated": "NonUpdated"
    };

    const stats = categories.map((cat) => {
      const items = filteredCompliance.filter((i) => i.criticality === cat);

      const statusCounts = Object.values(statusLabels).reduce((acc, label) => {
        acc[label] = 0;
        return acc;
      }, {});

      items.forEach((item) => {
        const status = item.current_compliance_status;
        const key = statusLabels[status];
        if (key) statusCounts[key]++;
      });

      return {
        category: cat,
        ...statusCounts,
      };
    });

    const finalStats = stats.map((row) => ({
      category: row.category,
      Complied: row.Complied || 0,
      Partial: row.Partial || 0,
      NotApplicable: row.NotApplicable || 0,
      NonComplied: row.NonComplied || 0,
      NonUpdated: row.NonUpdated || 0,
    }));

    setStatisticsRiskData(finalStats);
  }, [filteredCompliance]);

  useEffect(() => {
    console.log("complianceBarData", typeof complianceBarData);
  }, []);

  const STEP = 2000;
  const total = filteredCompliance.length; // 716

  const yMax = Math.ceil(total / STEP) * STEP; // 2000
  const yTicks = Array.from(
    { length: yMax / STEP + 1 },
    (_, i) => i * STEP
  );

  const columnOptions = useMemo(() => {
    const categories = (complianceBarData || []).map((d) => d.name);
    const toLabel = (key) =>
      key
        .replace(/([A-Z])/g, " $1")
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());

    return {
      chart: {
        type: "column",
        backgroundColor: "transparent",
        options3d: { enabled: true, alpha: 0, beta: 0, depth: 80, viewDistance: 25 },
        spacingBottom: 10,
        marginBottom: 120,
        height: 460,

      },
      title: { text: null },
      
      xAxis: {
        categories,
        labels: {
          rotation: 30,
          overflow: "allow",
          crop: false,
          align: "left",
          y: 18,
          x: 2,
          style: { fontSize: "12px", whiteSpace: "nowrap", textOverflow: "none", },

        },

      },
      yAxis: {
        title: { text: null },
        min: 0,
        max: yMax,
        tickInterval: 500,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        shared: true,
        formatter: function () {
          const lines = [`<b>${this.x}</b>`];
          this.points.forEach((pt) => {
            lines.push(`${toLabel(pt.series.name)}: <b>${pt.y}</b>`);
          });
          return lines.join("<br/>");
        },
        useHTML: true,
      },
      plotOptions: {
        series: {
          clip: false,
        },
        column: {
          depth: 40,
          stacking: "normal",
          borderWidth: 0,
          pointPadding: 0.1,
          groupPadding: 0.15,
        },
      },
      credits: { enabled: false },
      series: [
        {
          name: "complied",
          data: (complianceBarData || []).map((d) => d.complied || 0),
          color: "#28a745",
        },
        {
          name: "notComplied",
          data: (complianceBarData || []).map((d) => d.notComplied || 0),
          color: "#dc3545",
        },
        {
          name: "notApplicable",
          data: (complianceBarData || []).map((d) => d.notApplicable || 0),
          color: "#9ba0a3ff",
        },
        {
          name: "notUpdated",
          data: (complianceBarData || []).map((d) => d.notUpdated || 0),
          color: "#3074d2ff",
        },
        {
          name: "compiledWithException",
          data: (complianceBarData || []).map(
            (d) => d.compiledWithException || 0
          ),
          color: "#facc15",
        },
      ],
    };
  }, [complianceBarData]);





  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <main>
      <section className="lyt-section p-0">
        <Container fluid className="typ-container">
          <Row>
            <Col xs={12}>
              <div
                className=" mt-3"
                style={{
                  borderRadius: "10px",
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <span style={{ fontWeight: "bold", fontSize: "12px" }}>
                      Hello {user.name}!
                    </span>
                    <span
                      style={{
                        color: "#6c757d",
                        fontSize: "12px",
                        marginLeft: "5px",
                      }}
                    >
                      - here’s what’s happening with your panel.
                    </span>
                  </div>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "10px" }}
                  >
                    <ExportDropdown
                      formats={formats}
                      periods={periods}
                      exportReport={exportReport}
                      filteredReports={filteredReports}
                    />

                    {/* <div className="">
                      <button
                        className="btn btn-light dropdown-toggle w-100"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        Select Location
                      </button>
                      <ul
                        className="dropdown-menu p-2"
                        style={{ width: "270px" }}
                      >
                        <div className="border p-2 rounded mb-2">
                          <div>
                            {selectedLocation.length > 0
                              ? selectedLocation.map((item, index) => (
                                  <span
                                    key={index}
                                    className="bg-dark text-white px-1 rounded me-1"
                                  >
                                    {item}
                                  </span>
                                ))
                              : "Select Name"}
                          </div>
                        </div>
                        <li className="">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search"
                            value={locationSearchName}
                            onChange={(e) =>
                              setLocationSearchName(e.target.value)
                            }
                          />
                        </li>
                        <div className="mt-2 w-full">
                          {locations
                            ?.filter((c) =>
                              c
                                .toLowerCase()
                                .includes(locationSearchName.toLowerCase())
                            )
                            ?.map((location) => (
                              <li
                                className="form-check d-flex justify-content-between align-items-center py-1 border-bottom"
                                key={location}
                              >
                                <label className="form-check-label flex-grow-1 fs-6">
                                  {location}
                                </label>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={selectedLocation.includes(location)}
                                  onChange={() =>
                                    toggleLocationSelection(location)
                                  }
                                />
                              </li>
                            ))}
                        </div>
                      </ul>
                    </div> */}

                    <Dropdown
                      label="Location"
                      options={locations}
                      selectedItems={selectedLocation}
                      setSelectedItems={setSelectedLocation}
                    />

                    {/* <div className="dropdown">
                      <button
                        className="btn btn-light dropdown-toggle w-100"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        Select Company Name
                      </button>
                      <ul
                        className="dropdown-menu p-2"
                        style={{ width: "270px" }}
                      >
                        <div className="border p-2 rounded mb-2">
                          <div>
                            {selectedCompany.length > 0
                              ? selectedCompany.map((item, index) => (
                                <span
                                  key={index}
                                  className="bg-dark text-white px-1 rounded me-1"
                                >
                                  {item}
                                </span>
                              ))
                              : "Select Name"}
                          </div>
                        </div>
                        <li className="">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search"
                            value={companySearchName}
                            onChange={(e) =>
                              setCompanySearchName(e.target.value)
                            }
                          />
                        </li>
                        <div className="mt-2 w-full">
                          {companies
                            ?.filter((c) =>
                              c
                                .toLowerCase()
                                .includes(companySearchName.toLowerCase())
                            )
                            ?.map((company) => (
                              <li
                                className="form-check d-flex justify-content-between align-items-center py-1 border-bottom"
                                key={company}
                              >
                                <label className="form-check-label flex-grow-1 fs-6">
                                  {company}
                                </label>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={selectedCompany.includes(company)}
                                  onChange={() =>
                                    toggleCompanySelection(company)
                                  }
                                />
                              </li>
                            ))}
                        </div>
                      </ul>
                    </div> */}

                    <Dropdown
                      label="Company Name"
                      options={companies}
                      selectedItems={selectedCompany}
                      setSelectedItems={setSelectedCompany}
                    />

                    <div
                      className="position-relative d-inline-block dashboard1"
                      ref={dateDropdownRef}
                    >
                      <button
                        className="btn bg-white border border-secondary-subtle d-flex align-items-center gap-2 w-20"
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                        aria-expanded={isOpen}
                      >
                        <img
                          src="https://cdn-icons-png.flaticon.com/512/747/747310.png"
                          alt="Calendar Icon"
                          width="16"
                          height="16"
                        />
                        <span className="fs-12">
                          {filterDateWise.startDate?.toLocaleDateString("en-GB")} -{" "}
                          {filterDateWise.endDate?.toLocaleDateString("en-GB")}
                        </span>
                      </button>

                      {isOpen && (
                        <div
                          className="position-absolute mt-2 bg-white border shadow p-3"
                          style={{
                            zIndex: 1000,


                            width: "718px",
                            maxWidth: "95vw",
                            right: 0,
                            overflow: "hidden",
                            borderRadius: "8px",

                          }}
                        >
                          <div className="row">
                            {/* LEFT SIDE */}
                            <div className="col-md-3">
                              <ul className="list-group fs-12">
                                {["Today", "Yesterday", "Last 7 days", "Last 30 days", "Manual"].map(
                                  (label) => (
                                    <li
                                      key={label}
                                      className={`list-group-item fs-12 ${activeFilter === label
                                        ? "bg-secondary text-white rounded"
                                        : ""
                                        }`}
                                      style={{
                                        border: "none",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => handleDateQuickFilter(label)}
                                    >
                                      {label}
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>

                            {/* RIGHT SIDE */}
                            {activeFilter === "Manual" && (
                              <div className="col-md-9 border-start">
                                <div className="row">
                                  <div className="col-md-6">
                                    <DatePicker
                                      selected={filterDateWise.startDate}
                                      onChange={(date) =>
                                        setFilterDateWise((prev) => ({
                                          ...prev,
                                          startDate: date,
                                        }))
                                      }
                                      inline
                                    />
                                  </div>

                                  <div className="col-md-6">
                                    <DatePicker
                                      selected={filterDateWise.endDate}
                                      onChange={(date) =>
                                        setFilterDateWise((prev) => ({
                                          ...prev,
                                          endDate: date,
                                        }))
                                      }
                                      inline
                                    />
                                  </div>
                                </div>

                                <div className="mt-3 d-flex justify-content-between align-items-center">
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "6px",
                                    }}
                                  >
                                    <span
                                      className="font-s-12"
                                    >
                                      {filterDateWise.startDate?.toLocaleDateString("en-GB")}
                                    </span>

                                    <span className="fs-12">→</span>

                                    <span
                                      className="font-s-12"
                                    >
                                      {filterDateWise.endDate?.toLocaleDateString("en-GB")}
                                    </span>
                                  </div>

                                  <button
                                    className="font-s-12"
                                    onClick={() => applyDateFilter()}
                                  >
                                    Apply
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <div className="d-flex flex-wrap gap-3">
                <div className="bs-card flex-grow-1">
                  <div className="overview-div typ-round">
                    <div className="inner-view">
                      <div className="img-area">
                        <img src={company} alt="Total Companies" />
                      </div>
                      <div>
                        <p className="count color1">{totalCount.totalCompanies}</p>
                        <p className="sub-head fs-12">Total Companies</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bs-card flex-grow-1">
                  <div className="overview-div typ-round">
                    <div className="inner-view">
                      <div className="img-area typ-blue">
                        <img src={branches} alt="Total Branches" />
                      </div>
                      <div>
                        <p className="count color2">{totalCount.totalBranches}</p>
                        <p className="sub-head fs-12">Total Branches</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bs-card flex-grow-1">
                  <div className="overview-div typ-round">
                    <div className="inner-view">
                      <div className="img-area typ-green">
                        <img src={tick} alt="Closed Reports" />
                      </div>
                      <div>
                        <p className="count color3">{totalCount.totalClosedReports}</p>
                        <p className="sub-head fs-12">Closed Reports</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bs-card flex-grow-1">
                  <div className="overview-div typ-round">
                    <div className="inner-view">
                      <div className="img-area typ-orange">
                        <img src={activeReport} alt="Active Reports" />
                      </div>
                      <div>
                        <p className="count color4">{totalCount.totalActiveReports}</p>
                        <p className="sub-head fs-12">Active Reports</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={4}>
              <div className="bs-card mt-0">
                <div className="d-flex justify-content-between align-items-center mob-flex-colum">
                  <h2 className="fs-12 typ-overall ">OVERVIEW</h2>
                  <div className="dropdown-bx">
                    <select
                      className="form-select form-select-sm w-auto"
                      value={selectedFilter}
                      onChange={(e) => setSelectedFilter(e.target.value)}
                    >
                      {selectedFilter === null && (
                        <option value="Monthly fs-12" hidden>
                          Monthly
                        </option>
                      )}

                      <option value={7} className="fs-12">
                        Last seven days
                      </option>
                      <option value={14} className="fs-12">
                        Last two weeks
                      </option>
                      <option value={21} className="fs-12">
                        Last three weeks
                      </option>
                      <option value={30} className="fs-12">
                        Last one month
                      </option>
                    </select>
                  </div>
                </div>

                {circleChart &&
                  circleChart.length > 0 &&
                  circleChart.some((item) => item.value > 0) ? (
                  <>
                    <div className="main-pie fs-12" >
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={pieOptions}
                      />
                    </div>

                    <div className="legends">
                      {complianceStatuses.map(({ name, fill }) => (
                        <div key={name}>
                          <p
                            className="bs-status-txt bold fs-12"
                            style={{ color: fill }}
                          >
                            {name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      height: "287px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "#f9f9f9",
                      borderRadius: "8px",
                    }}
                  >
                    <p className="text-muted fs-12">No data available</p>
                  </div>
                )}
              </div>
            </Col>

            <Col lg={8}>
              <div className="bs-card mt-0">
                <div className="d-flex justify-content-between align-items-center w-100">
                  <h2 className=" typ-overall fs-12">COMPLIANCE CATEGORIES</h2>
                  <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center">
                      <span
                        className="rounded-circle d-inline-block me-1 bg-success"
                        style={{ width: "6px", height: "6px" }}
                      ></span>
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        Complied
                      </span>
                    </div>
                    <div className="d-flex align-items-center">
                      <span
                        className="rounded-circle d-inline-block me-1 bg-warning"
                        style={{ width: "6px", height: "6px" }}
                      ></span>
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        Partial Complied
                      </span>
                    </div>
                    <div className="d-flex align-items-center">
                      <span
                        className="rounded-circle d-inline-block me-1 bg-secondary"
                        style={{ width: "6px", height: "6px" }}
                      ></span>
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        Not Applicable
                      </span>
                    </div>
                    <div className="d-flex align-items-center">
                      <span
                        className="rounded-circle d-inline-block me-1 bg-danger"
                        style={{ width: "6px", height: "6px" }}
                      ></span>
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        Not Complied
                      </span>
                    </div>
                       <div className="d-flex align-items-center">
                      <span
                        className="rounded-circle d-inline-block me-1 bg-primary"
                        style={{ width: "6px", height: "6px" }}
                      ></span>
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        Not Updated
                      </span>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-secondary fs-12">
                      Total: {totalCompliance}
                    </span>

                    <select
                      className="form-select form-select-sm w-auto"
                      value={complianceCategoryFilter}
                      onChange={(e) =>
                        setComplianceCategoryFilter(e.target.value)
                      }

                    >
                      {complianceCategoryFilter === null && (
                        <option value="Monthly" hidden className="fs-12">
                          Monthly
                        </option>
                      )}
                      <option value={7} className="fs-12">
                        Last seven days
                      </option>
                      <option value={14} className="fs-12">
                        Last two weeks
                      </option>
                      <option value={21} className="fs-12">
                        Last three weeks
                      </option>
                      <option value={30} className="fs-12">
                        Last one month
                      </option>
                    </select>
                  </div>
                </div>
                {totalCompliance !== 0 ? (

                 <div className="chart-x-scroll">
                     <div className="chart-inner">
                       <HighchartsReact
                         highcharts={Highcharts}
                         options={columnOptions}
                       />
                     </div>
                   </div>
                ) : (
                  <div
                    style={{
                      height: "287px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "#ff9f9",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  >
                    <p className="text-muted fs-12">No data available</p>
                  </div>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={16}>
              <div className="mt-4">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <h2 className="text-sm fs-12">Company Overview</h2>
                      <select
                        className="form-select form-select-sm w-auto fs-12"
                        value={companyOverviewFilter}
                        onChange={(e) =>
                          setCompanyOverviewFilter(e.target.value)
                        }
                        disabled={!companyOverviewData}
                      >
                        {companyOverviewFilter === null && (
                          <option value="Monthly" hidden>
                            Monthly
                          </option>
                        )}

                        <option value={7} className="fs-12">
                          Last seven days
                        </option>
                        <option value={14} className="fs-12">
                          Last two weeks
                        </option>
                        <option value={21} className="fs-12">
                          Last three weeks
                        </option>
                        <option value={30} className="fs-12">
                          Last one month
                        </option>
                      </select>
                    </div>
                    <div className="table-responsive mt-3 border rounded w-100">
                      <div className="bs-table typ-datable fs-12 typ-border">
                        <DataTable
                          className="fs-12"
                          columns={columns}
                          data={companyOverviewData}
                          fixedHeader
                          fixedHeaderScrollHeight="450px"
                          selectableRowsHighlight
                          highlightOnHover
                          searchable
                        />
                      </div>
                    </div>


                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={7} className="d-flex flex-column h-100">
              <div className="mt-4 flex-grow-1 d-flex flex-column h-100">
                <div className="card h-100 d-flex flex-column">
                  <div className="card-body d-flex flex-column flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center w-100  py-2 ">
                      <h2 className=" mb-0 flex-shrink-0 fs-12">
                        CRITICALITY RISK{" "}
                      </h2>
                      <div className="d-flex align-items-center gap-2">
                        <div className="dropdown-bx">
                          <select
                            className="form-select form-select-sm w-auto fs-12"
                            value={criticalityRiskFilter}
                            onChange={(e) =>
                              setCriticalityRiskFilter(e.target.value)
                            }
                          // disabled={!criticalityRiskData}
                          >
                            {criticalityRiskFilter === null && (
                              <option value="Monthly" hidden>
                                Monthly
                              </option>
                            )}

                            <option value={7} className="fs-12">
                              Last seven days
                            </option>
                            <option value={14} className="fs-12">
                              Last two weeks
                            </option>
                            <option value={21} className="fs-12">
                              Last three weeks
                            </option>
                            <option value={30} className="fs-12">
                              Last one month
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="table-responsive mt-3 border rounded w-100 flex-grow-1">
                      <div className="bs-table typ-datable fs-12 typ-border">
                        <DataTable
                          columns={criticalityRiskColumns}
                          data={criticalityRiskData}
                          fixedHeader
                          highlightOnHover
                          customStyles={{
                            headCells: {
                              style: {
                                backgroundColor: "#f0f0f0",
                                fontWeight: "bold",
                              },
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={5} className=" h-80 ">
              <div
                className="bg-white  shadow-md d-flex flex-column  mt-4 rounded border"
                style={{ height: "94%" }}
              >
                <div className="d-flex justify-content-between align-items-center w-100 px-4 py-2 ">
                  <h2 className="fs-12 mb-0 flex-shrink-0">RISK STATISTICS</h2>
                  <div className="d-flex align-items-center gap-2  fs-12">
                    <span className="text-secondary ">
                      Total: {filteredCompliance.length}
                    </span>
                    <select
                      className="form-select form-select-sm w-auto"
                      value={statisticsRiskFilter}
                      onChange={(e) => setStaticticsRiskFilter(e.target.value)}
                    >
                      {statisticsRiskFilter === null && (
                        <option value="Monthly" hidden className="fs-12">
                          Monthly
                        </option>
                      )}

                      <option value={7} className="fs-12">
                        Last seven days
                      </option>
                      <option value={14} className="fs-12">
                        Last two weeks
                      </option>
                      <option value={21} className="fs-12">
                        Last three weeks
                      </option>
                      <option value={30} className="fs-12">
                        Last one month
                      </option>
                    </select>
                  </div>
                </div>
                <div className="d-flex flex-grow-1 ">
                  <div className="flex-grow-1">
                    {/* <ResponsiveContainer height={270} width={490}>
                      <BarChart data={statisticsRiskData} barSize={8}>
                        <Legend
                          layout="horizontal"
                          verticalAlign="top"
                          iconType="circle"
                          wrapperStyle={{
                            fontSize: "12px",
                            paddingBottom: "12px",

                          }}
                        />
                        <XAxis className="fs-12" dataKey="category" />
                        {/* <YAxis domain={[0, "dataMax + 20"]} /> */}
                    <YAxis
                      width={45}
                      domain={[0, yMax]}
                      ticks={yTicks}
                      interval={0}
                      allowDecimals={false}
                      tick={{ fontSize: 12 }}
                    />




                    <Tooltip />

                    <Bar
                      dataKey="Complied"
                      fill="#16A34A"
                      name="Complied"
                      stackId="a"
                    />{" "}

                    <Bar
                      dataKey="Partial"
                      fill="#FACC15"
                      name="Partially Complied"
                      stackId="a"
                    />{" "}

                    <Bar
                      dataKey="NotApplicable"
                      fill="#9CA3AF"
                      name="Not Applicable"
                      stackId="a"
                    />

                    <Bar
                      dataKey="NonComplied"
                      fill="#DC2626"
                      name="Not Complied"
                      stackId="a"
                    />{" "}


                    <div style={{ width: '100%', maxWidth: 490, minWidth: 0 }}>
                      <ResponsiveContainer width="100%" height={290}>
                        <BarChart
                          data={statisticsRiskData}
                          barCategoryGap="20%"
                          barGap={4}
                        >
                          <Legend
                            layout="horizontal"
                            verticalAlign="top"
                            iconType="circle"
                            wrapperStyle={{
                              fontSize: "12px",
                              paddingBottom: "12px",
                            }}
                          />

                          <XAxis className="fs-12" dataKey="category" />
                          <YAxis domain={[0, "dataMax + 20"]} />

                          <Tooltip
                            cursor={{ fill: "transparent" }}
                            wrapperStyle={{
                              maxWidth: 220,
                              whiteSpace: "normal",
                              pointerEvents: "none",
                            }}
                            position={{ x: 20, y: 60 }}   // ✅ LOCK tooltip inside
                          />

                          <Bar dataKey="Complied" fill="#16A34A" />
                          <Bar dataKey="Partial" fill="#FACC15" />
                          <Bar dataKey="NotApplicable" fill="#9CA3AF" />
                          <Bar dataKey="NonComplied" fill="#DC2626" />
                          <Bar
                            dataKey="NonUpdated"
                            fill="#158ffaff"
                            name="Not Updated"
                          />{" "}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>


                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
}
export default Dashboard;
